import { FC } from 'react';

import { useTranslations } from '@telialt/common-cms/src/useTranslations';
import { TextButtonWithIcon } from '@telialt/common-text-button/src';

const TIMEOUT_TO_ENSURE_PATH_CHANGED = 300;
/**
 * List of regexps for referrer path which should force redirect to back url (category) instead of using history back
 */
const EXCEPTION_PATH_REGEXPS = [
    /\/prekes\/uzsakymas/,
    /\/privatiems\/uzsakymas/,
    /\/privatiems\/pirkimas/,
    /\/verslui\/uzsakymas/,
    /mano\/.*\/sso/,
];

/** based on Navigation API and on history.length
 * > Because Navigation API is *NEW* and may not be present in some Browsers
 * > ALSO history.length fails in cases when user can go forward, but not back -- to ensure
 * > we also check if location did change after firing history.back().
 * @param fallbackUrl - used when going back is restricted
 * @param timeout - timeout used to check location changed after first fire
 * @link https://developer.mozilla.org/en-US/docs/Web/API/Navigation/canGoBack */
const goBack = (fallbackUrl: string, timeout: number = TIMEOUT_TO_ENSURE_PATH_CHANGED) => {
    const oldLocation = window.location.pathname;

    // @ts-ignore ( TS2551: Property navigation does not exist on type Window & typeof globalThis )
    const canGoBack: boolean | undefined = window?.navigation?.canGoBack;

    if (canGoBack === true) {
        window.history.back();
    } else if (canGoBack === false || history.length <= 1) {
        window.location.assign(fallbackUrl);
    } else {
        window.history.back();
        setTimeout(() => {
            if (window.location.pathname === oldLocation) {
                window.location.assign(fallbackUrl);
            }
        }, timeout);
    }
};

const isNativeReferrer = (referrer: string) => {
    return referrer?.startsWith(window.location.origin);
};

const isExceptionReferrer = (referrer: string) => {
    return !!referrer && !!EXCEPTION_PATH_REGEXPS.some((re) => re.test(referrer));
};

type Props = { goBackUrl: string };

export const GoBackButton: FC<Props> = ({ goBackUrl = '/' }) => {
    const { t } = useTranslations();

    const handleBackClick = () => {
        if (isNativeReferrer(document.referrer) && !isExceptionReferrer(document.referrer)) {
            goBack(goBackUrl);
        } else {
            window.location.assign(goBackUrl);
        }
    };

    return (
        <TextButtonWithIcon icon="chevron-left" onClick={handleBackClick}>
            {t['eshop.product_page.back_link_text']}
        </TextButtonWithIcon>
    );
};
